import { useEffect } from 'react';

/**
 * Hook para ajustar la altura de la ventana en dispositivos móviles
 * Soluciona el problema de la barra de navegación móvil y 100vh
 */
export function useViewportHeight(): void {
  useEffect(() => {
    // Función para actualizar la altura
    const updateViewportHeight = () => {
      // Establece una variable CSS personalizada para la altura real de la ventana
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    
    // Actualizar al inicio
    updateViewportHeight();
    
    // Actualizar cuando cambia el tamaño de la ventana o la orientación
    window.addEventListener('resize', updateViewportHeight);
    window.addEventListener('orientationchange', updateViewportHeight);
    
    // Limpiar
    return () => {
      window.removeEventListener('resize', updateViewportHeight);
      window.removeEventListener('orientationchange', updateViewportHeight);
    };
  }, []);
}

/**
 * Hook para ajustar el desplazamiento en iOS cuando los campos de formulario tienen el foco
 * Soluciona el problema de los formularios que quedan ocultos detrás del teclado virtual
 */
export function useIOSKeyboardFix(): void {
  useEffect(() => {
    const inputs = document.querySelectorAll('input, textarea, select');
    
    const handleFocus = () => {
      // Pequeño retraso para asegurarse de que el teclado está visible
      setTimeout(() => {
        // Desplazar para asegurarnos de que el elemento está visible
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      }, 300);
    };
    
    inputs.forEach(input => {
      input.addEventListener('focus', handleFocus);
    });
    
    return () => {
      inputs.forEach(input => {
        input.removeEventListener('focus', handleFocus);
      });
    };
  }, []);
}

/**
 * Hook para detectar y gestionar el tema de color del dispositivo
 */
export function useColorScheme(): string {
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = () => {
      if (mediaQuery.matches) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    };
    
    // Comprobar inicialmente
    handleChange();
    
    // Escuchar cambios
    mediaQuery.addEventListener('change', handleChange);
    
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);
  
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Hook para detectar si el dispositivo es táctil
 */
export function useTouchDevice(): boolean {
  useEffect(() => {
    const isTouchDevice = 'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      ('msMaxTouchPoints' in navigator && (navigator as { msMaxTouchPoints: number }).msMaxTouchPoints > 0);
      
    if (isTouchDevice) {
      document.body.classList.add('touch-device');
    }
    
    return () => {
      document.body.classList.remove('touch-device');
    };
  }, []);
  
  return 'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    ('msMaxTouchPoints' in navigator && (navigator as { msMaxTouchPoints: number }).msMaxTouchPoints > 0);
}
